/**
 * Texte des Blinkhinweises je Meldungscode. Das Backend liefert Code und Werte
 * (`CriticalIssueDto.type`, `parameters`); Titel und Nachricht aus dem Backend sind nur Rückfall für
 * unbekannte Codes. `{count}` und `{sources}` werden ersetzt; `_one` gilt bei genau einem Eintrag.
 */
type CriticalIssueTexts = Record<string, string>;

/** Parameter value that selects the singular text variant. */
export const SINGULAR_COUNT = "1";
const CAUSE_PARAMETER = "cause";

const de: CriticalIssueTexts = {
  failure_circuits_blocked_title: "{count} Störungen: {sources}",
  failure_circuits_blocked_title_one: "Störung: {sources}",
  failure_circuits_blocked_message: "{cause}",
  cause_access: "Der zentrale Zugang wurde abgelehnt oder ist abgelaufen.",
  cause_funds: "Das zentrale Guthaben oder Kontingent ist erschöpft.",
  cause_unreachable: "Der zentrale Dienst ist nicht erreichbar.",
  cause_throttled: "Der zentrale Dienst begrenzt die Aufrufe.",
  cause_deterministic: "Die Verarbeitung ist bis zur technischen Prüfung angehalten.",
  incidents_open_title: "{count} Störungen: {problem}",
  incidents_open_title_one: "Störung: {problem}",
  incidents_open_message: "{operation}",
  migration_running_title: "Datenbank wird aktualisiert",
  migration_running_message: "Die Anwendung wird gerade aktualisiert. Fachliche Funktionen sind vorübergehend gesperrt.",
  migration_failed_title: "Datenbankmigration fehlgeschlagen",
  migration_failed_message: "Die Datenbank konnte nicht aktualisiert werden. Die Migration muss geprüft werden.",
  database_unavailable_title: "Datenbank nicht erreichbar",
  database_unavailable_message: "Die Anwendung kann die Datenbank nicht erreichen.",
  schema_mismatch_title: "Datenbankschema passt nicht zur Anwendung",
  schema_mismatch_message: "Das reale Datenbankschema entspricht nicht dem aktuellen Anwendungsmodell.",
  pending_migrations_title: "Datenbankmigrationen stehen aus",
  pending_migrations_message: "Es gibt ausstehende Migrationen, die nicht beim Start angewendet werden.",
  critical_startup_error_title: "Kritischer Fehler beim Start",
  critical_startup_error_message: "Beim Start ist ein kritischer Fehler aufgetreten.",
  startup_problem_title: "Systemstart nicht abgeschlossen",
  startup_problem_message: "Der Systemstart ist nicht im Zustand bereit.",
  open: "ansehen",
};

const en: CriticalIssueTexts = {
  failure_circuits_blocked_title: "{count} disruptions: {sources}",
  failure_circuits_blocked_title_one: "Disruption: {sources}",
  failure_circuits_blocked_message: "{cause}",
  cause_access: "The central credentials were rejected or have expired.",
  cause_funds: "The central balance or quota has been exhausted.",
  cause_unreachable: "The central service is unreachable.",
  cause_throttled: "The central service is limiting requests.",
  cause_deterministic: "Processing is on hold pending technical investigation.",
  incidents_open_title: "{count} disruptions: {problem}",
  incidents_open_title_one: "Disruption: {problem}",
  incidents_open_message: "{operation}",
  migration_running_title: "Database is being updated",
  migration_running_message: "The application is being updated. Business functions are temporarily locked.",
  migration_failed_title: "Database migration failed",
  migration_failed_message: "The database could not be updated. The migration needs to be checked.",
  database_unavailable_title: "Database unreachable",
  database_unavailable_message: "The application cannot reach the database.",
  schema_mismatch_title: "Database schema does not match the application",
  schema_mismatch_message: "The actual database schema does not match the current application model.",
  pending_migrations_title: "Database migrations pending",
  pending_migrations_message: "There are pending migrations that are not applied on startup.",
  critical_startup_error_title: "Critical startup error",
  critical_startup_error_message: "A critical error occurred during startup.",
  startup_problem_title: "Startup not completed",
  startup_problem_message: "The system startup is not ready.",
  open: "open",
};

export const criticalIssueTranslations: Record<string, CriticalIssueTexts> = { de, en, ru: en, uk: en, it: en };

export function translateCriticalIssue(
  language: string,
  type: string,
  part: "title" | "message",
  parameters: Record<string, string> | null | undefined,
): string | null {
  const texts = criticalIssueTranslations[language] ?? en;
  const count = parameters?.count;
  const template = (part === "title" && count === SINGULAR_COUNT ? texts[`${type}_${part}_one`] : undefined)
    ?? texts[`${type}_${part}`];
  if (!template) return null;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => name === CAUSE_PARAMETER
    ? texts[`cause_${parameters?.cause}`] ?? ""
    : parameters?.[name] ?? "");
}

export function criticalIssueText(language: string, key: string): string {
  return (criticalIssueTranslations[language] ?? en)[key] ?? en[key] ?? key;
}
